/*
 * markdownish — the small markdown subset Ada actually emits.
 *
 * Shared by the transcript and by the tiered answer's prose and summary tiers,
 * so there is exactly one renderer for **bold**, `code`, headings, lists and
 * pipe tables. Line-based rather than block-based because the model mixes forms
 * inside one block — a lead-in sentence followed by bullets is its commonest
 * shape, and a block parser turns that into a paragraph with literal dashes.
 *
 * Full markdown is not worth a dependency; leaving these raw looks broken.
 */

import React from "react";

const RE_HEADING = /^(#{1,4})\s+(.*)$/;
const RE_BULLET = /^\s*[-*]\s+(.*)$/;
const RE_ORDERED = /^\s*\d+[.)]\s+(.*)$/;
const RE_RULE = /^\s*(-{3,}|_{3,}|\*{3,})\s*$/;
const RE_TABLE_SEP = /^[\s|:-]+$/;

// Models routinely write markdown into a JSON string field with ESCAPED
// newlines — the two characters backslash-n rather than a real break. Left
// alone, a line-based parser sees one enormous line: no headings, no tables,
// and the escapes rendered as visible text. Normalise before parsing; a literal
// backslash-n is never what prose actually wanted.
function normalise(text) {
  return String(text)
    .replace(/\r\n?/g, "\n")
    .replace(/\\r\\n|\\n/g, "\n")
    .replace(/\\t/g, "  ");
}

export function renderMarkdownish(text) {
  const lines = normalise(text).split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  const cells = (row) => {
    const parts = row.split("|").map((c) => c.trim());
    if (parts.length && parts[0] === "") parts.shift();
    if (parts.length && parts[parts.length - 1] === "") parts.pop();
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    if (RE_RULE.test(line)) { out.push(<hr className="aw-hr" key={key++} />); i++; continue; }

    const h = RE_HEADING.exec(line);
    if (h) {
      const Tag = `h${Math.min(h[1].length + 1, 5)}`;
      out.push(<Tag className={`aw-h${h[1].length}`} key={key++}>{inline(h[2])}</Tag>);
      i++;
      continue;
    }

    // Table: a header row followed by a |---|---| separator.
    if (line.includes("|") && RE_TABLE_SEP.test(lines[i + 1] || "") && (lines[i + 1] || "").includes("-")) {
      const head = cells(line);
      const body = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|")) { body.push(cells(lines[i])); i++; }
      out.push(
        <div className="aw-tablewrap" key={key++}>
          <table className="aw-table">
            <thead><tr>{head.map((c, n) => <th key={n}>{inline(c)}</th>)}</tr></thead>
            <tbody>{body.map((r, rn) => <tr key={rn}>{r.map((c, n) => <td key={n}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    if (RE_BULLET.test(line)) {
      const items = [];
      while (i < lines.length && RE_BULLET.test(lines[i])) { items.push(RE_BULLET.exec(lines[i])[1]); i++; }
      out.push(<ul className="aw-ul" key={key++}>{items.map((t, n) => <li key={n}>{inline(t)}</li>)}</ul>);
      continue;
    }

    if (RE_ORDERED.test(line)) {
      const items = [];
      while (i < lines.length && RE_ORDERED.test(lines[i])) { items.push(RE_ORDERED.exec(lines[i])[1]); i++; }
      out.push(<ol className="aw-ol" key={key++}>{items.map((t, n) => <li key={n}>{inline(t)}</li>)}</ol>);
      continue;
    }

    // Paragraph: run until something structural starts.
    const para = [];
    while (
      i < lines.length && lines[i].trim() &&
      !RE_HEADING.test(lines[i]) && !RE_BULLET.test(lines[i]) &&
      !RE_ORDERED.test(lines[i]) && !RE_RULE.test(lines[i]) &&
      !(lines[i].includes("|") && RE_TABLE_SEP.test(lines[i + 1] || ""))
    ) { para.push(lines[i]); i++; }
    out.push(
      <p key={key++}>
        {para.map((l, n) => <React.Fragment key={n}>{n > 0 && <br />}{inline(l)}</React.Fragment>)}
      </p>
    );
  }
  return out;
}

export function inline(s) {
  const parts = String(s).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i}>{p.slice(1, -1)}</code>;
    return p;
  });
}

